/**
 * ComingSoonModal — small "this tab isn't wired yet" gate per Lead's
 * Apr 28 2026 brief: "Users can't use any tab yet. When they press on
 * a tab a box pops up coming soon."
 *
 * Tabs that DO have working pages (World, My Roots, My Profile, Work
 * Center, Admin) bypass this modal. Everything else (Country History,
 * State, City, Monuments & Symbols, About Us, Contact Us, etc.) opens
 * this gate when clicked.
 */
import { useEffect } from 'react';
import { X, Hourglass } from 'lucide-react';

export default function ComingSoonModal({ open, label, onClose }) {
  // ESC closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center px-4 backdrop-blur-sm"
      style={{ background: 'rgba(0,0,0,0.78)' }}
      data-testid="coming-soon-modal"
      onClick={onClose}
    >
      <div
        className="relative max-w-md w-full bg-[#0A0A0A] border border-[#D4A84A]/35 rounded-md shadow-2xl px-6 py-7 md:px-8 md:py-9"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="coming-soon-close"
          className="absolute top-3 right-3 text-white/55 hover:text-[#D4A84A] transition-colors p-1"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        <div className="flex items-center justify-center w-14 h-14 mx-auto mb-5 rounded-full border border-[#D4A84A]/40 bg-[#D4A84A]/8">
          <Hourglass size={24} className="text-[#D4A84A]" />
        </div>

        <div className="text-center">
          <p className="font-mono text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#D4A84A]/85 mb-2">
            Coming Soon
          </p>
          <h3 className="font-historical text-[#D4A84A] text-2xl md:text-3xl font-light tracking-tight mb-4" data-testid="coming-soon-label">
            {label || 'This page'}
          </h3>
          <p className="font-mono text-sm md:text-base text-white/80 leading-relaxed mb-2">
            We're still building this part of the chain.
          </p>
          <p className="font-mono text-xs md:text-sm text-white/55 leading-relaxed">
            What you can walk today: the spine of human origins —
            from the first walker to the symbolic leap. Press <span className="text-[#D4A84A]">Start Your Journey</span>{' '}
            on the timeline to begin.
          </p>
        </div>

        <div className="mt-7 flex justify-center">
          <button
            onClick={onClose}
            data-testid="coming-soon-ok"
            className="font-hud text-xs tracking-widest text-[#0A0A0A] bg-[#D4A84A] px-5 py-2.5 hover:bg-[#D4A84A]/85 min-h-[40px]"
          >
            GOT IT
          </button>
        </div>
      </div>
    </div>
  );
}
